import {
  CheckboxVisibility,
  DetailsListLayoutMode,
  DetailsRow,
  FontIcon,
  IColumn,
  ICommandBarItemProps,
  IDetailsRowProps,
  IDropdownOption,
  Link,
  MessageBar,
  MessageBarType,
  SelectionMode,
  ShimmeredDetailsList,
  TextField,
} from '@fluentui/react';
import * as React from 'react';

import { IProvisioningStatusItem } from '../../../Common/ProvisioningModels/ProvisioningStatus';
import { commandBarRefreshItem } from '../../../Common/Components/RefreshButton';
import { sortItems } from '../../../Common/Components/SortItems';
import * as Styled from '../../../Common/Styles/portalPage.styled';
import * as StatusStyled from '../../../Common/Styles/portalManagement.styled';
import { useProvisioningStatus } from '../../../Hooks/Provisioning/useProvisioningStatus';
import { ProvisioningDetailPanel } from '../ProvisioningDetailPanel/ProvisioningDetailPanel';

const searchFieldOptions: IDropdownOption[] = [
  { key: 'subjectId', text: 'Subject ID' },
  { key: 'correlationId', text: 'Request ID' },
];

export function ProvisioningStatusPage(): React.ReactElement {
  const [search, setSearch] = React.useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = React.useState<string>('');
  const [searchField, setSearchField] = React.useState<string>('subjectId');
  const [selectedItem, setSelectedItem] = React.useState<IProvisioningStatusItem | null>(null);
  const [items, loading, hasNextPage, loadMore, refresh] = useProvisioningStatus(debouncedSearch, searchField);
  const [columns, setColumns] = React.useState<IColumn[]>(provisioningTableColumns);
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);

  // Auto-refresh on window focus
  React.useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const sortedItems = React.useMemo(() => {
    const sorted = [...items];
    const sortCol = columns.find((c) => c.isSorted);
    if (sortCol) {
      sortItems(sorted, sortCol.key, sortCol.isSortedDescending);
    }
    return sorted;
  }, [items, columns]);

  const onColumnClickSort = (_ev: React.MouseEvent<HTMLElement>, column: IColumn): void => {
    let isSortedDescending = column.isSortedDescending ?? false;
    if (column.isSorted) {
      isSortedDescending = !isSortedDescending;
    }

    setColumns((prev) =>
      prev.map((col) => ({
        ...col,
        isSorted: col.key === column.key,
        isSortedDescending: col.key === column.key ? isSortedDescending : col.isSortedDescending,
      }))
    );
  };

  const searchFieldLabel= searchFieldOptions.find((o) => o.key === searchField)?.text ?? searchField;

  const commandBarItems: ICommandBarItemProps[] = [commandBarRefreshItem];

  // Debounce search input
  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Infinite scroll via IntersectionObserver
  React.useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, loading, loadMore]);

  const cellStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', height: '100%' };
  const pillStyle: React.CSSProperties = {
    backgroundColor: '#eceef1',
    color: '#32383f',
    borderRadius: 4,
    padding: '1px 7px',
    fontSize: 11,
    fontWeight: 600,
    lineHeight: '18px',
    whiteSpace: 'nowrap',
  };
  const pillOverflowStyle: React.CSSProperties = {
    ...pillStyle,
    backgroundColor: '#e3e6e8',
  };

  const onRenderItemColumn = React.useCallback(
    (item: IProvisioningStatusItem, _index: number, column: IColumn): React.ReactNode => {
      if (column.key === 'subjectId') {
        return (
          <div style={cellStyle}>
            <Link onClick={() => setSelectedItem(item)}>{item.subjectId}</Link>
          </div>
        );
      }
      if (column.key === 'status') {
        const { bg, fg } = statusPillColors(item.status);
        return (
          <div style={cellStyle}>
            <span style={{
              backgroundColor: bg,
              color: fg,
              borderRadius: 4,
              padding: '1px 7px',
              fontSize: 11,
              fontWeight: 600,
              lineHeight: '18px',
              whiteSpace: 'nowrap',
              display: 'inline-block',
              minWidth: 70,
              textAlign: 'center',
            }}>
              {item.status}
            </span>
          </div>
        );
      }
      if (column.key === 'entityTypeNames') {
        const raw = item.entityTypeNames || item.entityTypeIds || '';
        const types = raw.split(',').map((s) => s.trim()).filter(Boolean);
        if (types.length === 0) return <div style={cellStyle}>—</div>;
        const visible = types.slice(0, 2);
        const remaining = types.length - 2;
        return (
          <div style={{ ...cellStyle, gap: 4, flexWrap: 'wrap' }} title={types.join(', ')}>
            {visible.map((t) => (
              <span key={t} style={pillStyle}>{t}</span>
            ))}
            {remaining > 0 && <span style={pillOverflowStyle}>+{remaining}</span>}
          </div>
        );
      }
      if (column.key === 'startedAt') {
        return (
          <div style={cellStyle}>
            {item.startedAt ? new Date(item.startedAt).toLocaleString() : '—'}
          </div>
        );
      }
      return <div style={cellStyle}>{item[column.key as keyof IProvisioningStatusItem] as string}</div>;
    },
    []
  );

  const onRenderRow = (props: IDetailsRowProps) => {
    return <DetailsRow aria-busy={true} {...props} />;
  };

  return (
    <div>
      <title>Provisioning Status</title>
      <Styled.PortalPageHeadingSection style={{ flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
        <Styled.PortalPageTitleText>Provisioning Status</Styled.PortalPageTitleText>
        <Styled.PortalPageSubtitleText>
          {debouncedSearch
            ? `Searched by ${searchFieldLabel}: ${debouncedSearch}`
            : 'Last 7 Days'}
        </Styled.PortalPageSubtitleText>
      </Styled.PortalPageHeadingSection>
      <Styled.ButtonsTableSection>
        <StatusStyled.PortalManagementFilterDiv>
          <Styled.CompactPortalCommandBar items={commandBarItems} ariaLabel="Command Bar" />
          <FontIcon aria-label="filter" iconName="Filter" />
          <label>Search by</label>
          <StatusStyled.DropdownStyled
            placeholder="Search field"
            ariaLabel="Search field"
            onChange={(_, option) => { if (option) setSearchField(option.key as string); }}
            selectedKey={searchField}
            options={searchFieldOptions}
            multiSelect={false}
          />
          <label>Value</label>
          <TextField
            onChange={(_, newValue) => setSearch(newValue ?? '')}
            placeholder={`Enter ${searchFieldLabel}...`}
            value={search}
            styles={{ root: { width: 300 } }}
          />
        </StatusStyled.PortalManagementFilterDiv>

        {!loading && items.length === 0 ? (
          <MessageBar
            messageBarType={MessageBarType.info}
            styles={{ root: { marginTop: 20 } }}
          >
            No provisioning records found.
          </MessageBar>
        ) : (
          <>
            <ShimmeredDetailsList
              items={sortedItems}
              enableShimmer={loading && items.length === 0}
              columns={columns}
              onRenderItemColumn={onRenderItemColumn}
              onRenderRow={onRenderRow}
              onColumnHeaderClick={onColumnClickSort}
              selectionMode={SelectionMode.none}
              layoutMode={DetailsListLayoutMode.justified}
              checkboxVisibility={CheckboxVisibility.hidden}
              ariaLabelForShimmer="Provisioning status is being fetched"
              ariaLabelForGrid="Provisioning Status Table"
            />
            <div ref={sentinelRef} style={{ height: 1 }} />
            {loading && items.length > 0 && (
              <div style={{ padding: '16px 0', textAlign: 'center', color: '#605e5c' }}>
                Loading more...
              </div>
            )}
          </>
        )}
      </Styled.ButtonsTableSection>

      <ProvisioningDetailPanel item={selectedItem} onDismiss={() => setSelectedItem(null)} />
    </div>
  );
}

function statusPillColors(status: string): { bg: string; fg: string } {
  switch (status) {
    case 'Succeeded': return { bg: '#107c10', fg: '#ffffff' };
    case 'Failed': return { bg: '#d13438', fg: '#ffffff' };
    case 'PartiallySucceeded': return { bg: '#c19c00', fg: '#ffffff' };
    case 'InProgress':
    case 'PipelineRunning': return { bg: '#0078d4', fg: '#ffffff' };
    case 'Queued': return { bg: '#6e7781', fg: '#ffffff' };
    default: return { bg: '#6e7781', fg: '#ffffff' };
  }
}

const provisioningTableColumns: IColumn[] = [
  {
    key: 'subjectId',
    name: 'ID',
    minWidth: 220,
    maxWidth: 280,
    onColumnClick: () => {},
  },
  {
    key: 'subjectType',
    name: 'Type',
    minWidth: 70,
    maxWidth: 90,
    onColumnClick: () => {},
  },
  {
    key: 'environment',
    name: 'Env',
    minWidth: 60,
    maxWidth: 80,
    onColumnClick: () => {},
  },
  {
    key: 'egressChannel',
    name: 'Channel',
    minWidth: 70,
    maxWidth: 90,
    onColumnClick: () => {},
  },
  {
    key: 'action',
    name: 'Action',
    minWidth: 60,
    maxWidth: 80,
    onColumnClick: () => {},
  },
  {
    key: 'entityTypeNames',
    name: 'Entity Types',
    minWidth: 180,
    maxWidth: 250,
    onColumnClick: () => {},
  },
  {
    key: 'startedAt',
    name: 'Started At',
    minWidth: 120,
    maxWidth: 147,
    isSorted: true,
    isSortedDescending: true,
    onColumnClick: () => {},
  },
  {
    key: 'status',
    name: 'Status',
    minWidth: 45,
    maxWidth: 60,
    onColumnClick: () => {},
  },
];
